import { useCallback, useMemo } from "react";

import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { ENABLE_FEATURE_FLAG_OVERRIDE_KEY } from "../../models/config";
import { useFeatureFlagStore } from "../use-feature-flag-store";

import { getFeatureFlagListQueryDocument } from "./feature-flag.gql";

interface UseFeatureFlagListProps {
  workspaceId: string;
  refetch?: boolean; // force a refetch from posthog (not the same as React Query cache)
  disableOverride?: boolean;
}

export const useFeatureFlagList = ({
  workspaceId,
  refetch = false,
  disableOverride = false,
}: UseFeatureFlagListProps) => {
  const { featureFlagList: featureFlagListStore } = useFeatureFlagStore();

  const query = useQuery({
    queryKey: queryKeys.featureFlags.list(workspaceId),
    queryFn: async () => {
      const client = createOperationClient("GetFeatureFlagList");
      const data = await client.request(getFeatureFlagListQueryDocument, {
        getFeatureFlagListInput: {
          workspaceId,
          refetch,
        },
      });
      return data.featureFlagList;
    },
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const getFeatureFlag = useCallback(
    (id: string) => featureFlagListStore.find((f) => f.id === id),
    [featureFlagListStore],
  );

  const canOverride = useMemo(() => {
    return query.data?.find((flag) => flag.id === ENABLE_FEATURE_FLAG_OVERRIDE_KEY)?.enabled;
  }, [query.data]);

  const featureFlagList = useMemo(() => {
    const data = query.data;
    if (!data) {
      return [];
    }

    return data.map((flag) => {
      const featureFlagOverride = getFeatureFlag(flag.id);
      const shouldOverride = !disableOverride && canOverride && featureFlagOverride;

      try {
        const payload = flag.payload ? JSON.parse(flag.payload) : null;
        return {
          ...flag,
          enabled: shouldOverride ? featureFlagOverride.enabled : flag.enabled,
          payload,
        };
      } catch (_error) {
        return {
          ...flag,
          enabled: shouldOverride ? featureFlagOverride.enabled : flag.enabled,
          payload: null,
        };
      }
    });
  }, [query.data, canOverride, getFeatureFlag, disableOverride]);

  return {
    featureFlagList,
    refetch: query.refetch,
    loading: query.isLoading,
    error: query.error,
    query,
  };
};
